import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useTheme } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { 
  ArrowLeft, Calculator, FileText, Download, Printer,
  Euro, Percent, Sparkles, User, Car,
  Check, AlertTriangle, Loader2, Moon, Sun, History, Info
} from "lucide-react";
import logoMcom from "@/assets/logo192.png";
import storage from "@/utils/storage";
import { MGSupportPanel } from "@/components/MGSupportPanel";
import { MGSupportResult } from "@/components/MGSupportResult";
import { SkodaConditionsPanel } from "@/components/SkodaConditionsPanel";
import { CupraConditionsPanel } from "@/components/CupraConditionsPanel";
import { CupraResult } from "@/components/CupraResult";
import { SuzukiConditionsPanel } from "@/components/SuzukiConditionsPanel";
import { SuzukiResult } from "@/components/SuzukiResult";
import { KiaConditionsPanel, KIA_GRIDS } from "@/components/KiaConditionsPanel";
import { KiaResult } from "@/components/KiaResult";
import { SeatConditionsPanel } from "@/components/SeatConditionsPanel";
import { SeatResult } from "@/components/SeatResult";
import { OmodaCompanyRule, getOmodaCompanyChannels } from "@/components/OmodaCompanyRule";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BRAND_COLORS = {
  SEAT: "#E31E24",    // Rouge
  CUPRA: "#CD7F32",   // Bronze
  SKODA: "#4BA82E",   // Vert
  KIA: "#212E38",     // Bleu foncé
  SUZUKI: "#164194",  // Bleu
  MG: "#212E38",      // Bleu foncé
  OMODA_JAECOO: "#FF6B00", // Orange
  CHERY: "#1A1A1A",   // Noir Chery
};

export default function CalculatorPage() {
  const { brand } = useParams();
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  
  const brandColor = BRAND_COLORS[brand] || "#0A0A0A";

  // Get session from localStorage
  const [session, setSession] = useState(null);
  
  // Selected model with its available aids
  const [selectedModel, setSelectedModel] = useState(null);

  // Models state
  const [models, setModels] = useState([]);
  const modelsRequest = useRef(0);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    model_name: "",
    price_ttc: "",
    discount_euro: "",
    discount_percent: "",
    has_trade_in: false,
    has_reprise_mini: false,
    has_boost_jpo: false,
    has_kialiberty: false,
    has_boost_po: true,  // Boost PO activé par défaut
    has_boost_livraison: false,
    has_prime_fidelite: false,
    has_paint_aid: false, // Aide peinture métal (Omoda&Jaecoo)
    is_vd_sale: false,
    is_vdr_sale: false,
    company_type: "NON",
    mg_b2b01_tier: "",
    skoda_renewal: null,
    is_deal_sale: false,
    cupra_context: null,
    kia_context: null,
    seat_registration_date: "",
    exceptional_aid: "",
    exceptional_aid_percent: "",
    custom_company_aid: "", // Aide société saisie libre pour PSTE R/Y
    // Suzuki Connect option
    is_suzuki_connected: true,  // Par défaut connecté
    // Commission Payplan 2026
    sale_type: "STOCK", // STOCK, COMMANDE, VD, VDR
    financing_type: "Sans financement",
    financing_amount: "", // CAE
    delivery_package: "PAS VENDU",
    waxyol_type: "NON", // NON, BASIC, CERAMIQUE
    extended_warranty: false,
    accessories_ttc: "",
    maintenance_contract: false,
    // Seller info
    seller_name: "",
  });

  // Types de société avec description (varient selon la marque)
  const getCompanyTypes = () => {
    // Types communs à la plupart des marques
    const commonTypes = [
      { value: "NON", label: "Particulier" },
    ];
    
    // Types spécifiques par marque
    const brandSpecificTypes = {
      SEAT: [
        { value: "PLIB", label: "PLIB (Prof. Libéral)" },
        { value: "PSTE_X", label: "PSTE X (PME cat. X)" },
        { value: "PSTEL", label: "PSTE L (PME cat. L)" },
        { value: "PSTER", label: "PSTE R (Grands comptes protocolés)" },
        { value: "PSTE_RY", label: "PSTE R/Y (Saisie libre)" },
        { value: "OPTA", label: "OPTA (Opérateur A)" },
        { value: "OPAE", label: "OPAE (Opérateur AE)" },
        { value: "LCD", label: "LCD (Location Courte Durée)" },
      ],
      CUPRA: [
        { value: "PLIB", label: "PLIB (Prof. Libéral)" },
        { value: "PSTE_X", label: "PSTE X (PME cat. X)" },
        { value: "PSTEL", label: "PSTE L (PME cat. L)" },
        { value: "PSTER", label: "PSTE R (PME cat. R)" },
        { value: "PSTE_RY", label: "PSTE R/Y (Saisie libre)" },
        { value: "OPTA", label: "OPTA (Opérateur A)" },
        { value: "LCD", label: "LCD (Location Courte Durée)" },
      ],
      SKODA: [
        { value: "PLIB", label: "PLIB (Prof. Libéral)" },
        { value: "PSTE_X", label: "PSTE X (PME cat. X)" },
        { value: "PSTEL", label: "PSTE L (PME cat. L)" },
        { value: "PSTE_RY", label: "PSTE R/Y (Saisie libre)" },
        { value: "OPTA", label: "OPTA (Opérateur A)" },
        { value: "OPAE", label: "OPAE (Opérateur AE)" },
        { value: "LCD", label: "LCD (Location Courte Durée)" },
        { value: "COLL", label: "COLL (Collectivités)" },
        { value: "RENT", label: "RENT" },
      ],
      KIA: [
        ...KIA_GRIDS,
        { value: "PROTOCOLE_GC", label: "Protocole Grand Compte (Saisie libre)" },
      ],
      SUZUKI: [
        { value: "PNP", label: "PNP (Prof. Nom Propre)" },
        { value: "VIP", label: "VIP" },
        { value: "PSTE_1_9", label: "Sociétés/Flottes 1-9 véhicules" },
        { value: "PSTE_10", label: "Sociétés/Flottes 10+ véhicules" },
        { value: "LLD", label: "LLD Suzuki Lease" },
        { value: "LLD_PROTO", label: "LLD Loueurs Protocolés" },
        { value: "LCD", label: "LCD (Location Courte Durée)" },
        { value: "AUTO_ECOLE", label: "Auto-École (Swift uniquement)" },
      ],
      MG: [
        { value: "B2B01", label: "B2B01 (Flottes)" },
        { value: "B2B02", label: "B2B02 (Prof. Libéral)" },
        { value: "B2B03", label: "B2B03 (Autres)" },
      ],
      OMODA_JAECOO: getOmodaCompanyChannels(selectedModel),
      CHERY: [
        { value: "B2B_HORS_LLD", label: "Ventes B2B réseau hors LLD" },
        { value: "LLD_MB", label: "LLD Marque Blanche (OJ Lease)" },
        { value: "AUTRES_LLD", label: "Autres LLD" },
      ],
    };
    
    return [...commonTypes, ...(brandSpecificTypes[brand] || [])];
  };

  const COMPANY_TYPES = getCompanyTypes();

  // Result state
  const [result, setResult] = useState(null);

  // Export state
  const [exporting, setExporting] = useState(false);
  
  // Edit mode state (from history)
  const [isEditMode, setIsEditMode] = useState(false);
  
  // Conditions commerciales - dates disponibles
  const [conditionsDates, setConditionsDates] = useState([]);
  const [selectedConditionsDate, setSelectedConditionsDate] = useState("current");

  useEffect(() => {
    // Get session
    const parsedSession = storage.getJSON("emcom_session");
    if (parsedSession) {
      setSession(parsedSession);
    }
    let dateId = "current";
    try {
      const savedEdit = JSON.parse(localStorage.getItem("emcom_edit_calculation") || "null");
      if (savedEdit?.type === "vn" && savedEdit.data?.brand === brand) {
        dateId = savedEdit.data.conditions_date_id || "current";
      }
    } catch { /* Ignore invalid historical navigation data. */ }
    setSelectedConditionsDate(dateId);
    fetchModels(dateId);
    fetchConditionsDates();
  }, [brand]);

  // Fetch available conditions dates for this brand
  const fetchConditionsDates = async () => {
    try {
      const response = await axios.get(`${API}/conditions/dates/${brand}`);
      setConditionsDates(response.data.dates || []);
    } catch (error) {
      console.error("Error fetching conditions dates:", error);
      // Not critical, just won't show the selector
    }
  };

  // Load calculation from history if available
  useEffect(() => {
    // Do not consume history data before its conditions/models have finished loading.
    if (loading) return;
    try {
      const editData = localStorage.getItem("emcom_edit_calculation");
      if (editData) {
        const { type, data } = JSON.parse(editData);
        if (type === "vn" && data.brand === brand) {
          setIsEditMode(true);
          setSelectedConditionsDate(data.conditions_date_id || "current");
          const needsExactMGTrim = ["MG", "SKODA", "CUPRA", "SUZUKI"].includes(brand) && !models.some((m) => m.name === data.model_name);
          // Pre-fill form with saved data
          setFormData(prev => ({
            ...prev,
            model_name: needsExactMGTrim ? "" : (data.model_name || ""),
            price_ttc: data.price_ttc?.toString() || "",
            discount_euro: data.discount_ttc?.toString() || "",
            discount_percent: "",
            has_trade_in: data.trade_in_bonus > 0,
            has_boost_po: data.boost_po > 0,
            has_boost_livraison: data.boost_livraison > 0,
            has_prime_fidelite: data.prime_fidelite > 0,
            is_vd_sale: data.sale_category === "VD",
            is_vdr_sale: data.sale_category === "VDR",
            company_type: data.company_type || "NON",
            custom_company_aid: ["PSTE_RY", "PROTOCOLE_GC"].includes(data.company_type)
              ? String(data.custom_company_aid ?? (data.price_ht_without_transport > 0 ? Number((data.company_aid / data.price_ht_without_transport * 100).toFixed(4)) : 0)) : "",
            mg_b2b01_tier: data.mg_b2b01_tier || "",
            skoda_renewal: data.skoda_renewal || null,
            is_deal_sale: data.sale_category === "DEAL",
            cupra_context: data.cupra_context || null,
            kia_context: data.kia_context || null,
            seat_registration_date: data.seat_registration_date || "",
            has_kialiberty: data.has_kialiberty ?? (data.kialiberty_prime > 0),
            has_reprise_mini: data.reprise_mini_bonus > 0,
            is_suzuki_connected: data.is_suzuki_connected ?? true,
            has_boost_jpo: data.suzuki_booster_requested ?? (data.boost_jpo > 0),
            exceptional_aid: data.exceptional_aid ? (data.exceptional_aid * 1.2).toFixed(2) : "",
            exceptional_aid_percent: "",
            sale_type: data.sale_type || "STOCK",
            seller_name: data.seller_name || "",
          }));
          // Set selected model
          if (models.length > 0 && data.model_name) {
            const model = models.find(m => m.name === data.model_name);
            if (model) {
              setSelectedModel(model);
            } else {
              setFormData(prev => ({ ...prev, model_name: "" }));
              toast.warning(`Le modèle "${data.model_name}" n'existe plus dans les conditions actuelles. Veuillez en sélectionner un autre.`);
            }
          }
          // Set result to show previous calculation
          setResult(data);
          if (needsExactMGTrim) {
            toast.warning("Sélectionnez la finition actualisée pour recalculer ce véhicule.");
          }
          if (models.find(m => m.name === data.model_name)) {
            toast.info("Calcul chargé depuis l'historique - Modifiez les valeurs et recalculez");
          }
        }
        // Clear the edit data after loading
        localStorage.removeItem("emcom_edit_calculation");
      }
    } catch (e) {
      console.error("Error loading edit data:", e);
      localStorage.removeItem("emcom_edit_calculation");
    }
  }, [brand, models, loading]);

  const fetchModels = async (dateId = "current") => {
    const requestId = ++modelsRequest.current;
    setLoading(true);
    try {
      let response;
      if (dateId === "current") {
        response = await axios.get(`${API}/models?brand=${brand}`);
      } else {
        response = await axios.get(`${API}/models/by-date?brand=${brand}&date_id=${dateId}`);
      }
      if (requestId !== modelsRequest.current) return;
      setModels(response.data.models || []);
      // Reset selected model when conditions change
      setSelectedModel(null);
      setFormData(prev => ({ ...prev, model_name: "", skoda_renewal: null, cupra_context: null, is_deal_sale: false, kia_context: null, seat_registration_date: "" }));
      setResult(null);
    } catch (error) {
      if (requestId !== modelsRequest.current) return;
      console.error("Error fetching models:", error);
      toast.error("Erreur lors du chargement des modèles");
    } finally {
      if (requestId === modelsRequest.current) setLoading(false);
    }
  };

  // Handle conditions date change
  const handleConditionsDateChange = (dateId) => {
    setSelectedConditionsDate(dateId);
    setLoading(true);
    fetchModels(dateId);
    const selectedDate = conditionsDates.find(d => d.id === dateId);
    if (selectedDate && !selectedDate.is_current) {
      toast.info(`Chargement des conditions: ${selectedDate.label}`);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value,
      ...(["model_name", "company_type", "is_vd_sale", "is_vdr_sale", "sale_type"].includes(field) ? { skoda_renewal: null } : {}),
      ...(["model_name", "company_type", "is_vd_sale", "is_vdr_sale"].includes(field) ? { cupra_context: null, is_deal_sale: false } : {}),
      ...(field === "is_deal_sale" ? { cupra_context: null } : {}),
      ...(["model_name", "company_type", "is_vd_sale", "is_vdr_sale", "is_deal_sale"].includes(field) && brand === "SEAT" ? { seat_registration_date: "", has_trade_in: false } : {}),
      ...(["model_name", "company_type"].includes(field) && brand === "SUZUKI" ? { has_boost_jpo: false } : {}),
      ...(["model_name", "company_type", "is_vd_sale", "is_vdr_sale"].includes(field) && brand === "KIA" ? { kia_context: null, has_trade_in: false, has_kialiberty: false } : {}),
      ...(field === "has_kialiberty" && !value ? { kia_context: { ...prev.kia_context, liberty_renewal: false } } : {}),
      ...(field === "model_name" && brand === "OMODA_JAECOO" && prev.company_type !== "NON" && models.find((model) => model.name === value)?.company_rates?.[prev.company_type] === undefined ? { company_type: "NON" } : {}),
    }));
    setResult(null); // Clear result when input changes
    
    // When model changes, update selected model with its aids
    if (field === 'model_name') {
      const model = models.find(m => m.name === value);
      setSelectedModel(model || null);
    }
  };

  const handleCalculate = async () => {
    if (!formData.model_name || !formData.price_ttc) {
      toast.error("Veuillez sélectionner un modèle et entrer un prix TTC");
      return;
    }
    if (brand === "MG" && formData.company_type === "B2B01" && Object.keys(selectedModel?.mg_support?.b2b01_tiers || {}).length && !formData.mg_b2b01_tier) {
      toast.error("Sélectionnez le palier B2B01");
      return;
    }

    if (!formData.seller_name) {
      toast.error("Veuillez entrer votre nom");
      return;
    }
    if (brand === "SKODA" && selectedModel?.skoda_conditions && formData.company_type !== "NON" && formData.company_type !== "PSTE_RY" && selectedModel.company_rates?.[formData.company_type] === undefined) {
      toast.error("Catégorie société non renseignée pour ce modèle Škoda");
      return;
    }
    if (brand === "OMODA_JAECOO" && formData.company_type !== "NON" && selectedModel?.company_rates?.[formData.company_type] === undefined) {
      toast.error("Sélectionnez un canal société disponible dans ces conditions OMODA/JAECOO");
      return;
    }
    if (formData.skoda_renewal && (!formData.skoda_renewal.order_date || !formData.skoda_renewal.registration_date)) {
      toast.error("Renseignez les dates de commande et d’immatriculation du renouvellement");
      return;
    }
    if (selectedModel?.cupra_conditions) {
      if ((formData.is_vd_sale || formData.is_vdr_sale) && !formData.cupra_context?.registration_date) {
        toast.error("Renseignez la date d’immatriculation VD/VDR pour appliquer la prime du mois");
        return;
      }
      if (formData.cupra_context?.trade_in_type && formData.cupra_context.trade_in_type !== "NONE" && (!formData.cupra_context.order_date || !formData.cupra_context.aak_date)) {
        toast.error("Renseignez les dates de commande et AAK de la reprise Raval");
        return;
      }
    }
    if (selectedModel?.seat_conditions?.private_grid_verified && (formData.is_vd_sale || formData.is_vdr_sale) && !formData.seat_registration_date) {
      toast.error("Renseignez la date d’immatriculation SEAT pour la prime VD/VDR du mois");
      return;
    }

    setCalculating(true);
    try {
      // Convertir le pourcentage (5 → 0.05)
      const discountPercent = formData.discount_percent 
        ? parseFloat(formData.discount_percent) / 100 
        : 0;
      
      const payload = {
        brand,
        conditions_date_id: selectedConditionsDate,
        model_name: formData.model_name,
        price_ttc: parseFloat(formData.price_ttc) || 0,
        discount_euro: parseFloat(formData.discount_euro) || 0,
        discount_percent: discountPercent,
        has_trade_in: selectedModel?.cupra_conditions ? false : formData.has_trade_in,
        has_reprise_mini: selectedModel?.cupra_conditions ? false : formData.has_reprise_mini,
        has_boost_jpo: formData.has_boost_jpo,
        has_kialiberty: formData.has_kialiberty,
        has_boost_po: formData.has_boost_po,
        has_boost_livraison: formData.has_boost_livraison,
        has_prime_fidelite: formData.has_prime_fidelite,
        has_paint_aid: formData.has_paint_aid,
        is_vd_sale: formData.is_vd_sale,
        is_vdr_sale: formData.is_vdr_sale,
        company_type: formData.company_type,
        mg_b2b01_tier: formData.mg_b2b01_tier || null,
        skoda_renewal: formData.skoda_renewal,
        is_deal_sale: formData.is_deal_sale,
        cupra_context: formData.cupra_context ? Object.fromEntries(Object.entries(formData.cupra_context).map(([key, value]) => [key, value === "" ? null : value])) : null,
        kia_context: formData.kia_context,
        seat_registration_date: formData.seat_registration_date || null,
        exceptional_aid: parseFloat(formData.exceptional_aid) || 0,
        exceptional_aid_percent: parseFloat(formData.exceptional_aid_percent) || 0,
        custom_company_aid: parseFloat(formData.custom_company_aid) || 0,
        // Suzuki Connect
        is_suzuki_connected: formData.is_suzuki_connected,
        // Payplan 2026
        sale_type: formData.sale_type,
        financing_type: formData.financing_type,
        financing_amount: parseFloat(formData.financing_amount) || 0,
        delivery_package: formData.delivery_package,
        waxyol_type: formData.waxyol_type,
        extended_warranty: formData.extended_warranty,
        accessories_ttc: parseFloat(formData.accessories_ttc) || 0,
        maintenance_contract: formData.maintenance_contract,
        // Seller info
        seller_name: formData.seller_name,
        concession_id: session?.concession_id || "",
        // Fleet mode
        is_fleet: session?.is_fleet || false,
      };

      const response = await axios.post(`${API}/calculate`, payload);
      setResult(response.data);
      
      // Save calculation to history
      try {
        await axios.post(`${API}/calculations/save`, response.data);
      } catch (e) {
        console.error("Error saving calculation:", e);
      }
      
      toast.success("Calcul effectué avec succès");
    } catch (error) {
      console.error("Error calculating:", error);
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Données de calcul invalides : vérifiez les champs saisis");
    } finally {
      setCalculating(false);
    }
  };

  const handleExportPDF = async () => {
    if (!result) {
      toast.error("Veuillez d'abord effectuer un calcul");
      return;
    }

    setExporting(true);
    try {
      const response = await axios.post(
        `${API}/pdf/export`,
        { ...result, seller_name: formData.seller_name },
        { responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `marge_${brand}_${formData.model_name}_${new Date().toISOString().slice(0,10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("PDF exporté avec succès");
    } catch (error) {
      console.error("Error exporting PDF:", error);
      toast.error("Erreur lors de l'export PDF");
    } finally {
      setExporting(false);
    }
  };

  const handlePrint = async () => {
    if (!result) {
      toast.error("Veuillez d'abord effectuer un calcul");
      return;
    }
    
    setExporting(true);
    try {
      // Generate PDF first
      const response = await axios.post(
        `${API}/pdf/export`,
        { ...result, seller_name: formData.seller_name },
        { responseType: 'blob' }
      );
      
      // Create blob URL and open in new window for printing
      const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
      const pdfUrl = window.URL.createObjectURL(pdfBlob);
      
      // Open PDF in new window for printing
      const printWindow = window.open(pdfUrl, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      } else {
        // If popup blocked, download instead
        toast.info("Popup bloqué - téléchargement du PDF");
        const link = document.createElement('a');
        link.href = pdfUrl;
        link.setAttribute('download', `marge_${brand}_${formData.model_name}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    } catch (error) {
      console.error("Error generating PDF for print:", error);
      toast.error("Erreur lors de la génération du PDF");
    } finally {
      setExporting(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(value);
  };

  const formatPercent = (value) => {
    return `${value.toFixed(2)} %`;
  };

  // Convertir TTC en HT (division par 1.2)
  const toHT = (ttcValue) => {
    return Math.round((ttcValue / 1.2) * 100) / 100;
  };

  // Calcule le pourcentage par rapport au prix HT hors transport
  const calcPercent = (value) => {
    if (!result || !result.price_ht_without_transport || result.price_ht_without_transport === 0) return "";
    const percent = (value / result.price_ht_without_transport) * 100;
    return `(${percent.toFixed(2)}%)`;
  };

  // Calcule le pourcentage par rapport au prix TTC
  const calcPercentTTC = (value) => {
    if (!result || !result.price_ttc || result.price_ttc === 0) return "";
    const percent = (value / result.price_ttc) * 100;
    return `(${percent.toFixed(2)}%)`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-foreground"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="verbaere-header text-white print:hidden">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="p-2 hover:bg-white/10 rounded-md transition-colors" data-testid="back-link">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-3">
              <div 
                className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden bg-white logo-container shadow-md"
              >
                <img src={logoMcom} alt="Verbaere" className="w-8 h-8 object-contain" />
              </div>
              <div>
                <h1 className="text-xl font-heading font-black tracking-tight">{brand}</h1>
                <p className="text-xs text-gray-400">e-MCom by Verbaere Performance Suite</p>
              </div>
            </div>
          </div>
          {/* Actions header */}
          <div className="flex items-center gap-3">
            {/* History Link */}
            <Link 
              to="/history"
              className="flex items-center gap-2 px-3 py-2 rounded-md bg-white/10 hover:bg-white/20 transition-colors"
              data-testid="calculator-history-link"
            >
              <History className="w-4 h-4" />
              <span className="text-sm hidden sm:inline">Historique</span>
            </Link>
            {/* Dark Mode Toggle */}
            <Button 
              variant="ghost" 
              size="icon"
              className="text-white hover:bg-white/10" 
              onClick={toggleTheme}
              title={isDark ? "Mode clair" : "Mode sombre"}
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left Column - Calculator Form */}
          <div className="lg:col-span-7 space-y-6">
            {/* Vehicle Selection */}
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-lg font-heading">
                  <Car className="w-5 h-5" style={{ color: brandColor }} />
                  Véhicule
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Seller Name & Concession Info */}
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 p-4 rounded-lg border dark:border-gray-600">
                  <Label htmlFor="seller_name" className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Nom du vendeur
                  </Label>
                  <Input
                    id="seller_name"
                    type="text"
                    value={formData.seller_name}
                    onChange={(e) => handleInputChange("seller_name", e.target.value)}
                    className="mt-2 font-medium"
                    placeholder="Entrez votre nom"
                    data-testid="seller-name-input"
                  />
                  
                  {/* Date des conditions commerciales - toujours visible */}
                  <div className="mt-3">
                    <Label className="text-xs font-medium text-gray-600 dark:text-gray-400 flex items-center gap-2">
                      <History className="w-3 h-3" />
                      Date des conditions commerciales
                    </Label>
                    <Select 
                      value={selectedConditionsDate} 
                      onValueChange={handleConditionsDateChange}
                    >
                      <SelectTrigger className="mt-1 h-8 text-xs" data-testid="conditions-date-select">
                        <SelectValue placeholder="Conditions actuelles" />
                      </SelectTrigger>
                      <SelectContent>
                        {conditionsDates.length > 0 ? (
                          conditionsDates.map((date) => (
                            <SelectItem key={date.id} value={date.id} className="text-xs">
                              {date.is_current ? "📅 Conditions actuelles" : `📁 ${date.label}`}
                            </SelectItem>
                          ))
                        ) : (
                          <SelectItem value="current" className="text-xs">
                            📅 Conditions actuelles
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Concession name only */}
                  {session?.concession_name && (
                    <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                      📍 {session.concession_name}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="model" className="text-sm font-medium text-gray-700">Modèle</Label>
                    <Select 
                      value={formData.model_name} 
                      onValueChange={(v) => handleInputChange("model_name", v)}
                    >
                      <SelectTrigger id="model" data-testid="model-select">
                        <SelectValue placeholder="Sélectionner un modèle" />
                      </SelectTrigger>
                      <SelectContent>
                        {models.map((model) => (
                          <SelectItem key={model.id || model.name} value={model.name}>
                            <span className="flex items-center gap-2">
                              {model.name}
                              {model.info && <Info className="w-3.5 h-3.5 text-amber-500" />}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="price_ttc" className="text-sm font-medium text-gray-700">Prix TTC avec options</Label>
                    <div className="relative">
                      <Input
                        id="price_ttc"
                        type="number"
                        value={formData.price_ttc}
                        onChange={(e) => handleInputChange("price_ttc", e.target.value)}
                        className="pr-8 font-data"
                        placeholder="0"
                        data-testid="price-ttc-input"
                      />
                      <Euro className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                </div>

                {selectedModel?.info && (
                  <div
                    data-testid="model-info-banner"
                    className="flex items-start gap-3 rounded-lg border-l-4 border-amber-500 bg-amber-50 dark:bg-amber-950/30 p-3 mt-2"
                  >
                    <Info className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                    <div className="text-sm">
                      <p className="font-semibold text-amber-900 dark:text-amber-200 mb-0.5">
                        Condition particulière — {selectedModel.name}
                      </p>
                      <p className="text-amber-800 dark:text-amber-300 leading-snug">
                        {selectedModel.info}
                      </p>
                    </div>
                  </div>
                )}

                {/* Sale Type Section - EXCLUSIF */}
                <div className="mt-4 p-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg">
                  <p className="text-xs text-amber-800 font-medium mb-3 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Types de vente exclusifs (non cumulables)
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {/* Particulier (default) */}
                    <button
                      type="button"
                      onClick={() => {
                        handleInputChange("is_vd_sale", false);
                        handleInputChange("is_vdr_sale", false);
                        handleInputChange("company_type", "NON");
                      }}
                      className={`p-3 rounded-lg border-2 text-center transition-all ${
                        !formData.is_vd_sale && !formData.is_vdr_sale && formData.company_type === "NON"
                          ? "border-green-500 bg-green-50 text-green-800"
                          : "border-gray-200 bg-white hover:border-gray-300"
                      }`}
                      data-testid="sale-type-particulier"
                    >
                      <span className="text-sm font-medium">Particulier</span>
                      <p className="text-xs text-gray-500 mt-1">Marge standard</p>
                    </button>
                    
                    {/* VD */}
                    <button
                      type="button"
                      onClick={() => {
                        handleInputChange("is_vd_sale", true);
                        handleInputChange("is_vdr_sale", false);
                        handleInputChange("company_type", "NON");
                      }}
                      className={`p-3 rounded-lg border-2 text-center transition-all ${
                        formData.is_vd_sale
                          ? "border-blue-500 bg-blue-50 text-blue-800"
                          : "border-gray-200 bg-white hover:border-gray-300"
                      }`}
                      data-testid="sale-type-vd"
                    >
                      <span className="text-sm font-medium">VD</span>
                      <p className="text-xs text-gray-500 mt-1">Véhicule Démo</p>
                    </button>
                    
                    {/* VDR */}
                    <button
                      type="button"
                      onClick={() => {
                        handleInputChange("is_vd_sale", false);
                        handleInputChange("is_vdr_sale", true);
                        handleInputChange("company_type", "NON");
                      }}
                      className={`p-3 rounded-lg border-2 text-center transition-all ${
                        formData.is_vdr_sale
                          ? "border-purple-500 bg-purple-50 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200"
                          : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300"
                      }`}
                      data-testid="sale-type-vdr"
                    >
                      <span className="text-sm font-medium">{brand === "SKODA" && selectedModel?.skoda_conditions ? "VDP" : selectedModel?.suzuki_conditions ? "VK" : "VDR"}</span>
                      <p className="text-xs text-muted-foreground mt-1">{brand === "SKODA" && selectedModel?.skoda_conditions ? "Véhicule de prêt" : selectedModel?.suzuki_conditions ? "Véhicule kilométré" : "Véhicule Remplacement"}</p>
                    </button>
                    
                    {/* Société */}
                    <button
                      type="button"
                      onClick={() => {
                        handleInputChange("is_vd_sale", false);
                        handleInputChange("is_vdr_sale", false);
                        // Set first company type
                        const firstCompanyType = COMPANY_TYPES.find(t => t.value !== "NON");
                        handleInputChange("company_type", firstCompanyType?.value || "PLIB");
                      }}
                      className={`p-3 rounded-lg border-2 text-center transition-all ${
                        formData.company_type !== "NON" && !formData.is_vd_sale && !formData.is_vdr_sale
                          ? "border-orange-500 bg-orange-50 text-orange-800"
                          : "border-gray-200 bg-white hover:border-gray-300"
                      }`}
                      data-testid="sale-type-societe"
                    >
                      <span className="text-sm font-medium">Société</span>
                      <p className="text-xs text-gray-500 mt-1">Vente B2B</p>
                    </button>
                  </div>
                  
                  {/* Company Type Selector (only visible when Société is selected) */}
                  {formData.company_type !== "NON" && !formData.is_vd_sale && !formData.is_vdr_sale && (
                    <div className="mt-3">
                      <Label htmlFor="company_type" className="text-sm font-medium text-muted-foreground">Type société</Label>
                      <Select 
                        value={formData.company_type} 
                        onValueChange={(v) => {
                          handleInputChange("company_type", v);
                          // Réinitialiser l'aide personnalisée si on change de type
                          if (v !== "PSTE_RY" && v !== "PROTOCOLE_GC") {
                            handleInputChange("custom_company_aid", "");
                          }
                        }}
                      >
                        <SelectTrigger id="company_type" data-testid="company-type-select" className="mt-1 text-foreground dark:text-white bg-background dark:bg-gray-800">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {COMPANY_TYPES.filter(t => t.value !== "NON").map((type) => (
                            <SelectItem key={type.value} value={type.value} data-testid={`company-type-option-${type.value.toLowerCase()}`}
                              disabled={(brand === "SKODA" && !!selectedModel?.skoda_conditions && type.value !== "PSTE_RY" && selectedModel.company_rates?.[type.value] === undefined) ||
                                (brand === "SUZUKI" && !!selectedModel?.suzuki_conditions && selectedModel.company_rates?.[type.value] === undefined) ||
                                (brand === "KIA" && !!selectedModel?.kia_conditions && selectedModel.company_rates?.[type.value] === undefined) ||
                                (brand === "SEAT" && !!selectedModel?.seat_conditions && type.value !== "PSTE_RY" && selectedModel.company_rates?.[type.value] === undefined) ||
                                (brand === "CUPRA" && !!selectedModel?.cupra_conditions && (!selectedModel.cupra_conditions.company_eligible || (type.value !== "PSTE_RY" && selectedModel.company_rates?.[type.value] === undefined)))}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      
                      {/* Champ de saisie libre pour PSTE R/Y et Protocole Grand Compte */}
                      {(formData.company_type === "PSTE_RY" || formData.company_type === "PROTOCOLE_GC") && (
                        <div className="mt-3 p-3 bg-slate-100 dark:bg-slate-800 rounded-lg border border-slate-300 dark:border-slate-600">
                          <Label htmlFor="custom_company_aid" className="text-sm font-medium text-slate-800 dark:text-slate-200">
                            % protocole grand compte
                          </Label>
                          <Input
                            id="custom_company_aid"
                            type="number"
                            step="0.5"
                            placeholder="Ex: 25 pour 25%"
                            value={formData.custom_company_aid}
                            onChange={(e) => handleInputChange("custom_company_aid", e.target.value)}
                            className="mt-1 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                            data-testid="custom-company-aid-input"
                          />
                          <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                            Saisissez le pourcentage du protocole grand compte
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {brand === "MG" && selectedModel?.mg_support && !formData.is_vdr_sale && (
                  <MGSupportPanel model={selectedModel} formData={formData}
                    onLoyaltyChange={(value) => handleInputChange("has_prime_fidelite", value)}
                    onTierChange={(value) => handleInputChange("mg_b2b01_tier", value)} />
                )}
                {brand === "SKODA" && selectedModel?.skoda_conditions && (
                  <SkodaConditionsPanel model={selectedModel} form={formData} onChange={handleInputChange} />
                )}
                {brand === "CUPRA" && selectedModel?.cupra_conditions && (
                  <CupraConditionsPanel model={selectedModel} form={formData} onChange={handleInputChange} />
                )}
                {brand === "SUZUKI" && selectedModel?.suzuki_conditions && (
                  <SuzukiConditionsPanel model={selectedModel} form={formData} onChange={handleInputChange} />
                )}
                {brand === "KIA" && selectedModel?.kia_conditions && (
                  <KiaConditionsPanel model={selectedModel} form={formData} onChange={handleInputChange} />
                )}
                {brand === "SEAT" && selectedModel?.seat_conditions && (
                  <SeatConditionsPanel model={selectedModel} form={formData} onChange={handleInputChange} />
                )}
                {brand === "OMODA_JAECOO" && selectedModel && formData.company_type !== "NON" && (
                  <OmodaCompanyRule model={selectedModel} category={formData.company_type} />
                )}

                {/* Dynamic Aids Toggles - Based on selected model */}
                {!formData.is_vd_sale && !formData.is_vdr_sale && formData.company_type === "NON" && selectedModel && !selectedModel.mg_support && !selectedModel.cupra_conditions && !selectedModel.suzuki_conditions && !selectedModel.kia_conditions && !selectedModel.seat_conditions && (
                  <div className="mt-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg border border-green-200 dark:border-green-800">
                    <Label className="text-sm font-medium text-green-800 dark:text-green-200 mb-3 block">
                      Aides commerciales disponibles pour {selectedModel.name} <span className="text-xs font-normal">(montants HT)</span>
                    </Label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {/* Aide à la reprise - valeur déjà en HT */}
                      {selectedModel.trade_in_bonus > 0 && (
                        <div className={`flex items-center justify-between p-3 rounded-lg border-2 transition-colors ${
                          formData.has_trade_in 
                            ? 'bg-green-100 dark:bg-green-800/30 border-green-500' 
                            : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                        }`}>
                          <div className="flex-1">
                            <p className="text-xs font-medium text-muted-foreground">Aide Reprise</p>
                            <p className="text-sm font-bold">{selectedModel.trade_in_bonus.toLocaleString('fr-FR')} €</p>
                          </div>
                          <Switch
                            checked={formData.has_trade_in}
                            onCheckedChange={(v) => handleInputChange("has_trade_in", v)}
                            data-testid="trade-in-switch"
                          />
                        </div>
                      )}

                      {/* Aide reprise Mini - spécifique CUPRA RAVAL */}
                      {selectedModel.reprise_mini_bonus > 0 && (
                        <div
                          data-testid="reprise-mini-card"
                          className={`flex items-center justify-between p-3 rounded-lg border-2 transition-colors ${
                            formData.has_reprise_mini
                              ? 'bg-amber-100 dark:bg-amber-800/30 border-amber-500'
                              : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                          }`}
                        >
                          <div className="flex-1">
                            <p className="text-xs font-medium text-muted-foreground">Aide Reprise Mini</p>
                            <p className="text-sm font-bold">{selectedModel.reprise_mini_bonus.toLocaleString('fr-FR')} €</p>
                          </div>
                          <Switch
                            checked={!!formData.has_reprise_mini}
                            onCheckedChange={(v) => handleInputChange("has_reprise_mini", v)}
                            data-testid="reprise-mini-switch"
                          />
                        </div>
                      )}

                      {/* KIA Liberty - remplace la prime Particulier par la prime KiaLiberty (exclusif) */}
                      {brand === "KIA" && selectedModel.kialiberty_prime > 0 && (
                        <div
                          data-testid="kialiberty-card"
                          className={`flex items-center justify-between p-3 rounded-lg border-2 transition-colors ${
                            formData.has_kialiberty
                              ? 'bg-red-100 dark:bg-red-800/30 border-red-500'
                              : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                          }`}
                        >
                          <div className="flex-1">
                            <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                              Programme <span className="text-red-600 dark:text-red-400 font-bold">KiaLiberty</span>
                              <span className="px-1.5 py-0.5 text-[9px] font-semibold rounded bg-red-200 text-red-900 dark:bg-red-700 dark:text-red-50">
                                Type [12]
                              </span>
                            </p>
                            <p className="text-sm font-bold">
                              {selectedModel.kialiberty_prime.toLocaleString('fr-FR')} €
                              {selectedModel.systematic_prime > 0 && (
                                <span className="text-[10px] text-muted-foreground ml-2">
                                  (vs {selectedModel.systematic_prime.toLocaleString('fr-FR')} € en Particulier)
                                </span>
                              )}
                            </p>
                          </div>
                          <Switch
                            checked={!!formData.has_kialiberty}
                            onCheckedChange={(v) => handleInputChange("has_kialiberty", v)}
                            data-testid="kialiberty-switch"
                          />
                        </div>
                      )}

                      {/* Boost JPO / BOOSTER - opération limitée dans le temps */}
                      {selectedModel.boost_jpo > 0 && (
                        <div
                          data-testid="boost-jpo-card"
                          className={`flex items-center justify-between p-3 rounded-lg border-2 transition-colors ${
                            formData.has_boost_jpo
                              ? 'bg-emerald-100 dark:bg-emerald-800/30 border-emerald-500'
                              : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                          }`}
                        >
                          <div className="flex-1">
                            <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                              {brand === "SUZUKI" ? "BOOSTER" : brand === "CUPRA" ? "Final Lap" : "Boost JPO"}
                              <span className="px-1.5 py-0.5 text-[9px] font-semibold rounded bg-emerald-200 text-emerald-900 dark:bg-emerald-700 dark:text-emerald-50">
                                LIMITÉ
                              </span>
                            </p>
                            <p className="text-sm font-bold">{selectedModel.boost_jpo.toLocaleString('fr-FR')} €</p>
                          </div>
                          <Switch
                            checked={!!formData.has_boost_jpo}
                            onCheckedChange={(v) => handleInputChange("has_boost_jpo", v)}
                            data-testid="boost-jpo-switch"
                          />
                        </div>
                      )}
                      
                      {/* Boost PO - valeur déjà en HT */}
                      {selectedModel.boost_po > 0 && (
                        <div className={`flex items-center justify-between p-3 rounded-lg border-2 transition-colors ${
                          formData.has_boost_po 
                            ? 'bg-blue-100 dark:bg-blue-800/30 border-blue-500' 
                            : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                        }`}>
                          <div className="flex-1">
                            <p className="text-xs font-medium text-muted-foreground">Boost PO</p>
                            <p className="text-sm font-bold">{selectedModel.boost_po.toLocaleString('fr-FR')} €</p>
                          </div>
                          <Switch
                            checked={formData.has_boost_po}
                            onCheckedChange={(v) => handleInputChange("has_boost_po", v)}
                            data-testid="boost-po-switch"
                          />
                        </div>
                      )}
                      
                      {/* Boost Livraison - valeur déjà en HT */}
                      {selectedModel.boost_livraison > 0 && (
                        <div className={`flex items-center justify-between p-3 rounded-lg border-2 transition-colors ${
                          formData.has_boost_livraison 
                            ? 'bg-purple-100 dark:bg-purple-800/30 border-purple-500' 
                            : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                        }`}>
                          <div className="flex-1">
                            <p className="text-xs font-medium text-muted-foreground">Boost Livraison</p>
                            <p className="text-sm font-bold">{selectedModel.boost_livraison.toLocaleString('fr-FR')} €</p>
                          </div>
                          <Switch
                            checked={formData.has_boost_livraison}
                            onCheckedChange={(v) => handleInputChange("has_boost_livraison", v)}
                            data-testid="boost-livraison-switch"
                          />
                        </div>
                      )}
                      
                      {/* Prime Fidélité - valeur déjà en HT */}
                      {selectedModel.prime_fidelite > 0 && (
                        <div className={`flex items-center justify-between p-3 rounded-lg border-2 transition-colors ${
                          formData.has_prime_fidelite 
                            ? 'bg-amber-100 dark:bg-amber-800/30 border-amber-500' 
                            : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                        }`}>
                          <div className="flex-1">
                            <p className="text-xs font-medium text-muted-foreground">Prime Fidélité</p>
                            <p className="text-sm font-bold">{selectedModel.prime_fidelite.toLocaleString('fr-FR')} €</p>
                          </div>
                          <Switch
                            checked={formData.has_prime_fidelite}
                            onCheckedChange={(v) => handleInputChange("has_prime_fidelite", v)}
                            data-testid="prime-fidelite-switch"
                          />
                        </div>
                      )}
                    </div>
                    
                    {/* Aide Peinture Métal - Only for Omoda&Jaecoo */}
                    {brand === "OMODA_JAECOO" && selectedModel?.paint_aid_ttc > 0 && (
                      <div className="mt-4 pt-4 border-t border-orange-200 dark:border-orange-800">
                        <div className={`flex items-center justify-between p-3 rounded-lg border-2 transition-colors ${
                          formData.has_paint_aid 
                            ? 'bg-orange-100 dark:bg-orange-800/30 border-orange-500' 
                            : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                        }`}>
                          <div className="flex-1">
                            <p className="text-sm font-medium">
                              {formData.has_paint_aid ? "🎨 Aide peinture métal activée" : "🎨 Aide peinture métal"}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {selectedModel.paint_aid_ttc.toLocaleString('fr-FR')} € TTC ({(selectedModel.paint_aid_ttc / 1.2).toFixed(2)} € HT)
                            </p>
                            <p className="text-xs text-orange-600 dark:text-orange-400">Livraison Avril</p>
                          </div>
                          <Switch
                            checked={formData.has_paint_aid}
                            onCheckedChange={(v) => handleInputChange("has_paint_aid", v)}
                            data-testid="paint-aid-switch"
                          />
                        </div>
                      </div>
                    )}
                    
                    {/* Suzuki Connect Toggle - Only for Suzuki */}
                    {brand === "SUZUKI" && (
                      <div className="mt-4 pt-4 border-t border-green-200 dark:border-green-800">
                        <div className={`flex items-center justify-between p-3 rounded-lg border-2 transition-colors ${
                          formData.is_suzuki_connected 
                            ? 'bg-teal-100 dark:bg-teal-800/30 border-teal-500' 
                            : 'bg-orange-100 dark:bg-orange-800/30 border-orange-500'
                        }`}>
                          <div className="flex-1">
                            <p className="text-sm font-medium">
                              {formData.is_suzuki_connected ? "🔗 Suzuki Connect activé" : "⚡ Suzuki Connect non activé"}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {formData.is_suzuki_connected 
                                ? `Participation Suzuki: ${(selectedModel?.suzuki_connected_rate || 0.6) * 100}% (plafond ${toHT(selectedModel?.suzuki_connected_cap_ttc || 0).toLocaleString('fr-FR')}€ HT)`
                                : `Participation Suzuki: ${(selectedModel?.suzuki_non_connected_rate || 0.4) * 100}% (plafond ${toHT(selectedModel?.suzuki_non_connected_cap_ttc || 0).toLocaleString('fr-FR')}€ HT)`
                              }
                            </p>
                          </div>
                          <Switch
                            checked={formData.is_suzuki_connected}
                            onCheckedChange={(v) => handleInputChange("is_suzuki_connected", v)}
                            data-testid="suzuki-connect-switch"
                          />
                        </div>
                      </div>
                    )}
                    
                    {/* Message if no optional aids available */}
                    {!selectedModel.trade_in_bonus && !selectedModel.boost_po && !selectedModel.boost_livraison && !selectedModel.prime_fidelite && brand !== "SUZUKI" && (
                      <p className="text-sm text-muted-foreground italic">Aucune aide optionnelle configurée pour ce modèle</p>
                    )}
                  </div>
                )}
                
                {/* Message for VD/VDR sales */}
                {(formData.is_vd_sale || formData.is_vdr_sale) && !selectedModel?.skoda_conditions && !selectedModel?.cupra_conditions && !selectedModel?.suzuki_conditions && !selectedModel?.kia_conditions && !selectedModel?.seat_conditions && !(brand === "MG" && selectedModel?.mg_support && formData.is_vd_sale) && (
                  <div data-testid="vd-vdr-margin-rule" className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      {brand === "OMODA_JAECOO" && formData.is_vd_sale ? (
                        <><strong>Vente VD</strong> : aide VD{selectedModel ? ` de ${(selectedModel.vd_rate * 100).toLocaleString("fr-FR")} %` : ''}, sans cumul avec la marge de base.</>
                      ) : (
                        <><strong>Vente {formData.is_vd_sale ? 'VD' : 'VDR'}</strong> : seule la marge de base + l'aide {formData.is_vd_sale ? 'VD' : 'VDR'} sont appliquées</>
                      )}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Discount Section */}
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-lg font-heading">
                  <Percent className="w-5 h-5" style={{ color: brandColor }} />
                  Remise Client
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <Label htmlFor="discount_euro" className="text-sm font-medium text-muted-foreground">Remise en €</Label>
                    <div className="relative">
                      <Input
                        id="discount_euro"
                        type="number"
                        value={formData.discount_euro}
                        onChange={(e) => handleInputChange("discount_euro", e.target.value)}
                        className="pr-8 font-data"
                        placeholder="0"
                        data-testid="discount-euro-input"
                      />
                      <Euro className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="discount_percent" className="text-sm font-medium text-muted-foreground">OU Remise en %</Label>
                    <div className="relative">
                      <Input
                        id="discount_percent"
                        type="number"
                        value={formData.discount_percent}
                        onChange={(e) => handleInputChange("discount_percent", e.target.value)}
                        className="pr-8 font-data"
                        placeholder="Ex: 5 pour 5%"
                        step="0.1"
                        max="100"
                        data-testid="discount-percent-input"
                      />
                      <Percent className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="exceptional_aid" className="text-sm font-medium text-muted-foreground">Aide exceptionnelle en €</Label>
                    <div className="relative">
                      <Input
                        id="exceptional_aid"
                        type="number"
                        value={formData.exceptional_aid}
                        onChange={(e) => {
                          handleInputChange("exceptional_aid", e.target.value);
                          if (e.target.value) handleInputChange("exceptional_aid_percent", "");
                        }}
                        className="pr-8 font-data"
                        placeholder="0"
                        data-testid="exceptional-aid-input"
                      />
                      <Euro className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="exceptional_aid_percent" className="text-sm font-medium text-muted-foreground">OU Aide except. en %</Label>
                    <div className="relative">
                      <Input
                        id="exceptional_aid_percent"
                        type="number"
                        value={formData.exceptional_aid_percent}
                        onChange={(e) => {
                          handleInputChange("exceptional_aid_percent", e.target.value);
                          if (e.target.value) handleInputChange("exceptional_aid", "");
                        }}
                        className="pr-8 font-data"
                        placeholder="Ex: 2 pour 2%"
                        step="0.1"
                        max="100"
                        data-testid="exceptional-aid-percent-input"
                      />
                      <Percent className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Commission Section - Payplan 2026 / Fleet */}
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-lg font-heading">
                  <Sparkles className="w-5 h-5" style={{ color: brandColor }} />
                  {session?.is_fleet ? "Commission Fleet" : "Commission Vendeur (Payplan 2026)"}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Section Fleet simplifiée */}
                {session?.is_fleet ? (
                  <div className="space-y-4">
                    <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-4">
                      <p className="text-sm text-emerald-800 dark:text-emerald-200 font-medium">
                        🚚 Payplan Fleet - Commissions forfaitaires
                      </p>
                      <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
                        {["KIA", "CUPRA", "SEAT"].includes(brand) 
                          ? "KIA/CUPRA/SEAT : 120€ | Autres : ≥3% → 85€ | 2-3% → 50€ | <2% → 30€"
                          : "≥3% : 85€ | 2-3% : 50€ | <2% : 30€"
                        }
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex items-center justify-between space-x-2 bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
                        <Label htmlFor="fleet-delivery" className="text-sm cursor-pointer">Forfait livraison (20€)</Label>
                        <Switch
                          id="fleet-delivery"
                          checked={formData.delivery_package === "LIVRAISON 599"}
                          onCheckedChange={(checked) => handleInputChange("delivery_package", checked ? "LIVRAISON 599" : "PAS VENDU")}
                          data-testid="fleet-delivery-switch"
                        />
                      </div>
                      <div className="flex items-center justify-between space-x-2 bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
                        <Label htmlFor="fleet-waxyol" className="text-sm cursor-pointer">Waxyol 250€ (20€)</Label>
                        <Switch
                          id="fleet-waxyol"
                          checked={formData.waxyol_type === "FLEET"}
                          onCheckedChange={(checked) => handleInputChange("waxyol_type", checked ? "FLEET" : "NON")}
                          data-testid="fleet-waxyol-switch"
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Section Showroom standard */
                  <>
                    {/* Type de vente - Toggles */}
                    <div>
                      <Label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">Type de vente</Label>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => handleInputChange("sale_type", "COMMANDE")}
                          className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all border-2 ${
                            formData.sale_type === "COMMANDE"
                              ? "bg-blue-600 border-blue-600 text-white shadow-lg shadow-blue-200 dark:shadow-blue-900/50"
                              : "bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                          }`}
                          data-testid="sale-type-commande"
                        >
                          📝 Commande (6%)
                        </button>
                        <button
                          type="button"
                          onClick={() => handleInputChange("sale_type", "STOCK")}
                          className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all border-2 ${
                            formData.sale_type === "STOCK"
                              ? "bg-emerald-600 border-emerald-600 text-white shadow-lg shadow-emerald-200 dark:shadow-emerald-900/50"
                              : "bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
                          }`}
                          data-testid="sale-type-stock"
                        >
                          📦 Stock (10%)
                        </button>
                      </div>
                    </div>

                    {/* Options avec switches */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="flex items-center justify-between space-x-2 bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
                        <Label htmlFor="delivery" className="text-sm cursor-pointer">Forfait livraison (45€)</Label>
                        <Switch
                          id="delivery"
                          checked={formData.delivery_package === "LIVRAISON 599"}
                          onCheckedChange={(checked) => handleInputChange("delivery_package", checked ? "LIVRAISON 599" : "PAS VENDU")}
                          data-testid="delivery-switch"
                        />
                      </div>
                      <div className="flex items-center justify-between space-x-2 bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
                        <Label htmlFor="waxyol-basic" className="text-sm cursor-pointer">Waxyol Basic (20€)</Label>
                        <Switch
                          id="waxyol-basic"
                          checked={formData.waxyol_type === "BASIC"}
                          onCheckedChange={(checked) => handleInputChange("waxyol_type", checked ? "BASIC" : "NON")}
                          data-testid="waxyol-basic-switch"
                        />
                      </div>
                      <div className="flex items-center justify-between space-x-2 bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
                        <Label htmlFor="waxyol-ceramic" className="text-sm cursor-pointer">Waxyol Céramique (40€)</Label>
                        <Switch
                          id="waxyol-ceramic"
                          checked={formData.waxyol_type === "CERAMIQUE"}
                          onCheckedChange={(checked) => handleInputChange("waxyol_type", checked ? "CERAMIQUE" : "NON")}
                          data-testid="waxyol-ceramic-switch"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Accessoires TTC</Label>
                        <div className="relative mt-1">
                          <Input
                            type="number"
                            value={formData.accessories_ttc}
                            onChange={(e) => handleInputChange("accessories_ttc", e.target.value)}
                            className="pr-8 font-data"
                            placeholder="0"
                            data-testid="accessories-input"
                          />
                          <span className="absolute right-8 top-1/2 -translate-y-1/2 text-xs text-gray-400">5%</span>
                          <Euro className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex items-center justify-between space-x-2 bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
                        <Label htmlFor="warranty" className="text-sm cursor-pointer">Extension garantie (20€)</Label>
                        <Switch
                          id="warranty"
                          checked={formData.extended_warranty}
                          onCheckedChange={(v) => handleInputChange("extended_warranty", v)}
                          data-testid="warranty-switch"
                        />
                      </div>
                      <div className="flex items-center justify-between space-x-2 bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
                        <Label htmlFor="maintenance" className="text-sm cursor-pointer">Contrat entretien (20€)</Label>
                        <Switch
                          id="maintenance"
                          checked={formData.maintenance_contract}
                          onCheckedChange={(v) => handleInputChange("maintenance_contract", v)}
                          data-testid="maintenance-switch"
                        />
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Calculate Button */}
            <Button
              onClick={handleCalculate}
              disabled={calculating || !formData.model_name || !formData.price_ttc}
              className="w-full h-12 text-base font-medium uppercase tracking-wider"
              style={{ backgroundColor: brandColor }}
              data-testid="calculate-button"
            >
              {calculating ? (
                <span className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Calcul en cours...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Calculator className="w-5 h-5" />
                  Calculer la marge
                </span>
              )}
            </Button>
          </div>

          {/* Right Column - Results */}
          <div className="lg:col-span-5 space-y-6">
            {/* Results Card */}
            <Card className={`border-0 shadow-sm ${result ? 'ring-2' : ''}`} style={{ ringColor: brandColor }}>
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-lg font-heading">
                  <FileText className="w-5 h-5" style={{ color: brandColor }} />
                  Résultat du calcul
                </CardTitle>
              </CardHeader>
              <CardContent>
                {result ? (
                  <div className="space-y-4 animate-fade-in">
                    {/* Sale Category Badge */}
                    <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${
                      result.sale_category === "SOCIETE" ? "bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-200" :
                      result.sale_category === "VD" ? "bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200" :
                      result.sale_category === "VDR" ? "bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-200" :
                      "bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-200"
                    }`}>
                      {result.sale_category === "SOCIETE" && `Vente Société (${result.company_type})`}
                      {result.sale_category === "VD" && "Vente VD (Véhicule Démonstration)"}
                      {result.sale_category === "VDR" && (result.skoda_t4_applied ? "Vente VDP (Véhicule de prêt)" : result.suzuki_t4_applied ? "Vente VK (Véhicule kilométré)" : "Vente VDR (Véhicule de Remplacement)")}
                      {result.sale_category === "PARTICULIER" && "Vente Particulier"}
                      {result.sale_category === "DEAL" && "Vente DEAL"}
                    </div>

                    {/* Margin Section */}
                    <p data-testid="result-conditions-label" className="text-xs text-muted-foreground">{result.conditions_label || "Conditions actuelles"}</p>
                    <div className="bg-gray-50 dark:bg-gray-800 rounded-md p-4">
                      <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3">Calcul de Marge</h4>
                      <div className="space-y-2 text-sm text-gray-700 dark:text-gray-200">
                        <div className="flex justify-between">
                          <span>Prix HT</span>
                          <span className="font-data">{formatCurrency(result.price_ht)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Transport HT</span>
                          <span className="font-data">{formatCurrency(result.transport_ht)}</span>
                        </div>
                        <div className="flex justify-between text-muted-foreground text-xs">
                          <span>Prix HT hors transport (base calcul)</span>
                          <span className="font-data">{formatCurrency(result.price_ht_without_transport)}</span>
                        </div>
                        <Separator className="my-2" />
                        
                        {/* Show only relevant aids based on sale category */}
                        {result.cupra_t4_applied && <CupraResult result={result} formatCurrency={formatCurrency} calcPercent={calcPercent} />}
                        {result.suzuki_t4_applied && <SuzukiResult result={result} formatCurrency={formatCurrency} calcPercent={calcPercent} />}
                        {result.kia_october_applied && <KiaResult result={result} formatCurrency={formatCurrency} calcPercent={calcPercent} />}
                        {result.seat_october_applied && <SeatResult result={result} formatCurrency={formatCurrency} calcPercent={calcPercent} />}
                        {result.skoda_renewal_aid_ht > 0 && (
                          <div data-testid="skoda-renewal-result-row" className="flex flex-wrap justify-between gap-2 text-emerald-700 dark:text-emerald-300">
                            <span>Renouvellement Škoda · complément</span>
                            <span data-testid="skoda-renewal-result-amount" className="font-data">{formatCurrency(result.skoda_renewal_aid_ht)}</span>
                          </div>
                        )}
                        {result.mg_support_category && (
                          <MGSupportResult result={result} formatCurrency={formatCurrency} calcPercent={calcPercent} />
                        )}
                        {result.sale_category === "PARTICULIER" && !result.mg_support_category && !result.cupra_t4_applied && !result.suzuki_t4_applied && !result.kia_october_applied && !result.seat_october_applied && (
                          <>
                            <div className="flex justify-between">
                              <span>Marge base</span>
                              <span className="font-data">{formatCurrency(result.base_margin)} <span className="text-muted-foreground text-xs">{calcPercent(result.base_margin)}</span></span>
                            </div>
                            {/* Pour Suzuki particulier avec remise: afficher participation constructeur */}
                            {result.suzuki_participation > 0 ? (
                              <div className="flex justify-between bg-teal-50 dark:bg-teal-900/30 -mx-4 px-4 py-1">
                                <span className="text-teal-700 dark:text-teal-300">Participation Suzuki France</span>
                                <span className="font-data text-teal-700 dark:text-teal-300">{formatCurrency(result.suzuki_participation)} <span className="text-xs">{calcPercent(result.suzuki_participation)}</span></span>
                              </div>
                            ) : result.systematic_prime > 0 && (
                              <div className="flex justify-between">
                                <span>Prime systématique</span>
                                <span className="font-data">{formatCurrency(result.systematic_prime)} <span className="text-muted-foreground text-xs">{calcPercent(result.systematic_prime)}</span></span>
                              </div>
                            )}
                            {result.trade_in_bonus > 0 && (
                              <div className="flex justify-between bg-green-50 dark:bg-green-900/30 -mx-4 px-4 py-1">
                                <span className="text-green-700 dark:text-green-300">Aide à la reprise</span>
                                <span className="font-data text-green-700 dark:text-green-300">{formatCurrency(result.trade_in_bonus)} <span className="text-xs">{calcPercent(result.trade_in_bonus)}</span></span>
                              </div>
                            )}
                            {result.boost_po > 0 && (
                              <div className="flex justify-between bg-blue-50 dark:bg-blue-900/20 -mx-4 px-4 py-1">
                                <span className="text-blue-700 dark:text-blue-300">Boost PO</span>
                                <span className="font-data text-blue-700 dark:text-blue-300">{formatCurrency(result.boost_po)} <span className="text-xs">{calcPercent(result.boost_po)}</span></span>
                              </div>
                            )}
                            {result.boost_livraison > 0 && (
                              <div className="flex justify-between bg-purple-50 dark:bg-purple-900/20 -mx-4 px-4 py-1">
                                <span className="text-purple-700 dark:text-purple-300">Boost Livraison</span>
                                <span className="font-data text-purple-700 dark:text-purple-300">{formatCurrency(result.boost_livraison)} <span className="text-xs">{calcPercent(result.boost_livraison)}</span></span>
                              </div>
                            )}
                            {result.prime_fidelite > 0 && (
                              <div className="flex justify-between bg-amber-50 dark:bg-amber-900/20 -mx-4 px-4 py-1">
                                <span className="text-amber-700 dark:text-amber-300">Prime Fidélité</span>
                                <span className="font-data text-amber-700 dark:text-amber-300">{formatCurrency(result.prime_fidelite)} <span className="text-xs">{calcPercent(result.prime_fidelite)}</span></span>
                              </div>
                            )}
                            {result.paint_aid > 0 && (
                              <div className="flex justify-between bg-orange-50 dark:bg-orange-900/20 -mx-4 px-4 py-1">
                                <span className="text-orange-700 dark:text-orange-300">Aide peinture métal</span>
                                <span className="font-data text-orange-700 dark:text-orange-300">{formatCurrency(result.paint_aid)}</span>
                              </div>
                            )}
                            {result.exceptional_aid > 0 && (
                              <div className="flex justify-between">
                                <span>Aide exceptionnelle</span>
                                <span className="font-data">{formatCurrency(result.exceptional_aid)}</span>
                              </div>
                            )}
                          </>
                        )}
                        
                        {/* VD : composantes appliquées, sans marge de base pour OMODA/JAECOO. */}
                        {result.sale_category === "VD" && !result.mg_support_category && !result.cupra_t4_applied && !result.suzuki_t4_applied && !result.kia_october_applied && !result.seat_october_applied && (
                          <>
                            {!((result.brand === "OMODA_JAECOO" || (result.brand === "KIA" && result.model_name.startsWith("PV5"))) && result.base_margin === 0) && (
                              <div data-testid="vd-base-margin-row" className="flex justify-between">
                                <span>Marge base</span>
                                <span className="font-data">{formatCurrency(result.base_margin)} <span className="text-muted-foreground text-xs">{calcPercent(result.base_margin)}</span></span>
                              </div>
                            )}
                            
                            {/* KIA VD: Afficher 3 lignes séparées */}
                            {result.brand === "KIA" && result.kia_vd_systematic_prime > 0 ? (
                              <>
                                <div className="flex justify-between">
                                  <span>Prime systématique</span>
                                  <span className="font-data">{formatCurrency(result.kia_vd_systematic_prime)}</span>
                                </div>
                                <div className="flex justify-between bg-blue-50 dark:bg-blue-900/30 -mx-4 px-4 py-2">
                                  <span className="font-medium text-blue-800 dark:text-blue-200">Aide VD 3%</span>
                                  <span className="font-data text-blue-800 dark:text-blue-200">{formatCurrency(result.kia_vd_bonus_3pct)} <span className="text-blue-600 dark:text-blue-400 text-xs font-medium">{calcPercent(result.kia_vd_bonus_3pct)}</span></span>
                                </div>
                              </>
                            ) : (
                              <div data-testid="vd-aid-row" className="flex justify-between bg-blue-50 dark:bg-blue-900/30 -mx-4 px-4 py-2">
                                <span className="font-medium text-blue-800 dark:text-blue-200">Aide VD</span>
                                <span data-testid="vd-aid-amount" className="font-data text-blue-800 dark:text-blue-200">{formatCurrency(result.vd_aid)} <span data-testid="vd-aid-percent" className="text-blue-600 dark:text-blue-400 text-xs font-medium">{calcPercent(result.vd_aid)}</span></span>
                              </div>
                            )}
                            
                            {result.exceptional_aid > 0 && (
                              <div className="flex justify-between">
                                <span>Aide exceptionnelle</span>
                                <span className="font-data">{formatCurrency(result.exceptional_aid)}</span>
                              </div>
                            )}
                          </>
                        )}
                        
                        {/* VDR: UNIQUEMENT marge base + aide VDR */}
                        {result.sale_category === "VDR" && !result.cupra_t4_applied && !result.suzuki_t4_applied && !result.kia_october_applied && !result.seat_october_applied && (
                          <>
                            <div className="flex justify-between">
                              <span>Marge base</span>
                              <span className="font-data">{formatCurrency(result.base_margin)} <span className="text-muted-foreground text-xs">{calcPercent(result.base_margin)}</span></span>
                            </div>
                            <div className="flex justify-between bg-purple-50 dark:bg-purple-900/30 -mx-4 px-4 py-2">
                              <span className="font-medium text-purple-800 dark:text-purple-200">{result.skoda_t4_applied ? "Aide VDP (Véhicule de prêt)" : "Aide VDR (Véhicule Remplacement)"}</span>
                              <span className="font-data text-purple-800 dark:text-purple-200">{formatCurrency(result.vdr_aid)} <span className="text-purple-600 dark:text-purple-400 text-xs font-medium">{calcPercent(result.vdr_aid)}</span></span>
                            </div>
                            {result.exceptional_aid > 0 && (
                              <div className="flex justify-between">
                                <span>Aide exceptionnelle</span>
                                <span className="font-data">{formatCurrency(result.exceptional_aid)}</span>
                              </div>
                            )}
                          </>
                        )}
                        
                        {/* Société: Affichage selon la marque */}
                        {result.sale_category === "SOCIETE" && !result.mg_support_category && !result.cupra_t4_applied && !result.suzuki_t4_applied && !result.kia_october_applied && !result.seat_october_applied && (
                          <>
                            {/* Pour Suzuki: Marge base + Participation Suzuki France */}
                            {result.brand === "SUZUKI" ? (
                              <>
                                <div className="flex justify-between">
                                  <span>Marge base</span>
                                  <span className="font-data">{formatCurrency(result.base_margin)} <span className="text-muted-foreground text-xs">{calcPercent(result.base_margin)}</span></span>
                                </div>
                                <div className="flex justify-between bg-teal-50 dark:bg-teal-900/20 -mx-4 px-4 py-2">
                                  <span className="font-medium text-teal-800 dark:text-teal-200">Participation Suzuki France ({result.company_type})</span>
                                  <span className="font-data text-teal-800 dark:text-teal-200">{formatCurrency(result.company_aid)} <span className="text-teal-600 dark:text-teal-400 text-xs font-medium">{calcPercent(result.company_aid)}</span></span>
                                </div>
                              </>
                            ) : result.brand === "OMODA_JAECOO" ? (
                              <>
                                {result.base_margin > 0 && <div data-testid="omoda-company-base-row" className="flex justify-between">
                                  <span>Marge base</span>
                                  <span data-testid="omoda-company-base-amount" className="font-data">{formatCurrency(result.base_margin)} <span className="text-muted-foreground text-xs">{calcPercent(result.base_margin)}</span></span>
                                </div>}
                                <div data-testid="omoda-company-aid-row" className="flex flex-wrap justify-between gap-2 bg-orange-50 dark:bg-orange-900/20 -mx-4 px-4 py-2">
                                  <span className="font-medium text-orange-800 dark:text-orange-200">{result.omoda_company_aid_additive ? "Aide société" : "Marge société totale"} {result.company_type}</span>
                                  <span data-testid="omoda-company-aid-amount" className="font-data text-orange-800 dark:text-orange-200">{formatCurrency(result.company_aid)} <span className="text-orange-600 dark:text-orange-400 text-xs font-medium">{calcPercent(result.company_aid)}</span></span>
                                </div>
                              </>
                            ) : (
                              /* Autres marques: Uniquement aide société */
                              <div data-testid="company-total-margin-row" className="flex flex-wrap justify-between gap-2 bg-orange-50 dark:bg-orange-900/20 -mx-4 px-4 py-2">
                                <span className="font-medium text-orange-800 dark:text-orange-200">{result.skoda_t4_applied ? `Marge société ${result.company_type} · base incluse` : `Aide Société ${result.company_type} (seule aide)`}</span>
                                <span data-testid="company-total-margin-amount" className="font-data text-orange-800 dark:text-orange-200">{formatCurrency(result.company_aid)} <span className="text-orange-600 dark:text-orange-400 text-xs font-medium">{calcPercent(result.company_aid)}</span></span>
                              </div>
                            )}
                            {/* Aide exceptionnelle for SOCIETE sales */}
                            {result.exceptional_aid > 0 && (
                              <div className="flex justify-between bg-amber-50 dark:bg-amber-900/20 -mx-4 px-4 py-2">
                                <span className="font-medium text-amber-800 dark:text-amber-200">Aide exceptionnelle</span>
                                <span className="font-data text-amber-800 dark:text-amber-200">{formatCurrency(result.exceptional_aid)} <span className="text-amber-600 dark:text-amber-400 text-xs font-medium">{calcPercent(result.exceptional_aid)}</span></span>
                              </div>
                            )}
                          </>
                        )}
                        
                        <Separator className="my-2" />
                        <div className="flex justify-between font-medium text-base">
                          <span>Total Marge HT</span>
                          <span className="font-data" style={{ color: brandColor }}>{formatCurrency(result.total_margin_ht)}</span>
                        </div>
                        <div className="flex justify-between text-gray-500">
                          <span>% de marge</span>
                          <span className="font-data">{formatPercent(result.margin_percentage)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Discount Section */}
                    {result.discount_ttc > 0 && (
                      <div className="bg-orange-50 dark:bg-orange-900/20 rounded-md p-4">
                        <h4 className="text-sm font-medium text-orange-700 dark:text-orange-300 uppercase tracking-wide mb-3">Remise Client</h4>
                        <div className="space-y-2 text-sm text-orange-800 dark:text-orange-200">
                          <div className="flex justify-between">
                            <span>Remise TTC</span>
                            <span className="font-data text-orange-600 dark:text-orange-400">-{formatCurrency(result.discount_ttc)} <span className="text-orange-500 dark:text-orange-400 text-xs">{calcPercentTTC(result.discount_ttc)}</span></span>
                          </div>
                          <div className="flex justify-between font-medium">
                            <span>Prix final TTC</span>
                            <span className="font-data">{formatCurrency(result.final_price_ttc)}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Marge Nette HT Section - après remise et frais fixes */}
                    <div className={`rounded-md p-4 ${result.net_margin_ht >= 0 ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'bg-red-50 dark:bg-red-900/20'}`}>
                      <h4 className={`text-sm font-medium uppercase tracking-wide mb-3 ${result.net_margin_ht >= 0 ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-700 dark:text-red-300'}`}>
                        Marge Nette HT
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between items-center">
                          <span className={result.net_margin_ht >= 0 ? 'text-emerald-800 dark:text-emerald-200' : 'text-red-800 dark:text-red-200'}>
                            Marge nette (après remise et frais)
                          </span>
                          <div className="text-right">
                            <span className={`font-data font-bold text-lg ${result.net_margin_ht >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                              {formatCurrency(result.net_margin_ht)}
                            </span>
                            <span data-testid="net-margin-percentage" className={`ml-2 text-sm font-medium ${result.net_margin_ht >= 0 ? 'text-emerald-500 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'}`}>
                              ({result.net_margin_percentage >= 0 ? '+' : ''}{result.net_margin_percentage?.toFixed(2) || '0.00'} %)
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Commission Section - Payplan 2026 */}
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-md p-4">
                      <h4 className="text-sm font-medium text-blue-700 dark:text-blue-300 uppercase tracking-wide mb-3">
                        Commission Vendeur ({session?.is_fleet ? "Payplan Fleet" : "Payplan 2026"})
                      </h4>
                      
                      {/* Info Fleet si mode Fleet */}
                      {session?.is_fleet && (
                        <div className="mb-3 p-2 bg-emerald-100 dark:bg-emerald-900/30 border border-emerald-400 dark:border-emerald-600 rounded-md">
                          <p className="text-xs text-emerald-700 dark:text-emerald-300 font-medium">
                            🚚 Payplan Fleet
                          </p>
                          <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
                            {["KIA", "CUPRA", "SEAT"].includes(result.brand) 
                              ? "KIA/CUPRA/SEAT : Forfait 120€"
                              : "≥3% : 85€ | 2-3% : 50€ | <2% : 30€"
                            }
                          </p>
                        </div>
                      )}
                      
                      {/* Warning si marge en dessous du minimum concession */}
                      {!session?.is_fleet && result.is_below_min_margin && (
                        <div className="mb-3 p-2 bg-amber-100 dark:bg-amber-900/30 border border-amber-400 dark:border-amber-600 rounded-md">
                          <p className="text-xs text-amber-700 dark:text-amber-300 font-medium flex items-center gap-1">
                            ⚠️ Marge nette inférieure au minimum concession ({(result.concession_min_margin_rate * 100).toFixed(1)}%)
                          </p>
                        </div>
                      )}
                      
                      {/* Warning si marge en dessous de 3% (forfait 40€) - Pas en mode Fleet */}
                      {!session?.is_fleet && result.is_below_3_percent && (
                        <div className="mb-3 p-2 bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-600 rounded-md">
                          <p className="text-xs text-red-700 dark:text-red-300 font-medium flex items-center gap-1">
                            🚨 Marge nette inférieure à 3%
                          </p>
                          <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                            Commission réduite au forfait de 40€
                          </p>
                        </div>
                      )}
                      
                      <div className="space-y-2 text-sm text-blue-800 dark:text-blue-200">
                        {/* Commission sur marge */}
                        {result.margin_commission > 0 && (
                          <div className="flex justify-between">
                            <span>
                              {session?.is_fleet 
                                ? (["KIA", "CUPRA", "SEAT"].includes(result.brand) 
                                    ? "Commission Fleet forfait (KIA/CUPRA/SEAT)"
                                    : `Commission Fleet (${(result.payplan_margin_percentage ?? result.net_margin_percentage ?? 0) >= 3 ? "≥3% → 85€" : (result.payplan_margin_percentage ?? result.net_margin_percentage ?? 0) >= 2 ? "2-3% → 50€" : "<2% → 30€"})`)
                                : (result.is_below_3_percent 
                                    ? "Commission forfait (marge < 3%)" 
                                    : `Commission sur marge (${result.sale_type === "COMMANDE" ? "6%" : "10%"})`)
                              }
                            </span>
                            <span className={`font-data ${!session?.is_fleet && result.is_below_3_percent ? "text-red-600 dark:text-red-400" : ""}`}>
                              {formatCurrency(result.margin_commission)}
                            </span>
                          </div>
                        )}
                        {result.vd_commission > 0 && (
                          <div className="flex justify-between">
                            <span>Prime VD (fixe)</span>
                            <span className="font-data">{formatCurrency(result.vd_commission)}</span>
                          </div>
                        )}
                        {result.vdr_commission > 0 && (
                          <div className="flex justify-between">
                            <span>{result.skoda_t4_applied ? "Prime VDP (fixe)" : result.suzuki_t4_applied ? "Prime VK (fixe)" : "Prime VDR (fixe)"}</span>
                            <span className="font-data">{formatCurrency(result.vdr_commission)}</span>
                          </div>
                        )}
                        {result.delivery_commission > 0 && (
                          <div className="flex justify-between">
                            <span>Forfait livraison</span>
                            <span className="font-data">{formatCurrency(result.delivery_commission)}</span>
                          </div>
                        )}
                        {result.waxyol_commission > 0 && (
                          <div className="flex justify-between">
                            <span>Waxyol</span>
                            <span className="font-data">{formatCurrency(result.waxyol_commission)}</span>
                          </div>
                        )}
                        {result.warranty_commission > 0 && (
                          <div className="flex justify-between">
                            <span>Extension garantie</span>
                            <span className="font-data">{formatCurrency(result.warranty_commission)}</span>
                          </div>
                        )}
                        {result.accessories_commission > 0 && (
                          <div className="flex justify-between">
                            <span>Accessoires (5%)</span>
                            <span className="font-data">{formatCurrency(result.accessories_commission)}</span>
                          </div>
                        )}
                        {result.maintenance_commission > 0 && (
                          <div className="flex justify-between">
                            <span>Contrat entretien</span>
                            <span className="font-data">{formatCurrency(result.maintenance_commission)}</span>
                          </div>
                        )}
                        <Separator className="my-2" />
                        <div className="flex justify-between font-medium text-base">
                          <span>Commission totale vendeur</span>
                          <span className="font-data text-blue-700 dark:text-blue-300">{formatCurrency(result.total_commission)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Final Result */}
                    <div 
                      className={`rounded-md p-4 ${result.margin_after_commission >= 0 ? 'bg-green-50 dark:bg-green-900/20' : 'bg-red-50 dark:bg-red-900/20'}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {result.margin_after_commission >= 0 ? (
                            <Check className="w-5 h-5 text-green-600 dark:text-green-400" />
                          ) : (
                            <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
                          )}
                          <span className={`font-medium ${result.margin_after_commission >= 0 ? 'text-green-800 dark:text-green-200' : 'text-red-800 dark:text-red-200'}`}>Marge nette après commission</span>
                        </div>
                        <div className="text-right">
                          <span 
                            className={`text-xl font-data font-bold ${result.margin_after_commission >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}
                            data-testid="final-margin"
                          >
                            {formatCurrency(result.margin_after_commission)}
                          </span>
                          <span 
                            className={`ml-2 text-sm font-medium ${result.margin_after_commission >= 0 ? 'text-green-500 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`}
                          >
                            ({result.margin_after_commission_percent >= 0 ? '+' : ''}{result.margin_after_commission_percent?.toFixed(2) || '0.00'} %)
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-400">
                    <Calculator className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Remplissez le formulaire et cliquez sur "Calculer" pour voir les résultats</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Actions Section */}
            {result && (
              <Card className="border-0 shadow-sm print:hidden">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center gap-2 text-lg font-heading">
                    <FileText className="w-5 h-5" style={{ color: brandColor }} />
                    Actions
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Print Button - génère le PDF et ouvre pour impression */}
                  <Button
                    onClick={handlePrint}
                    className="w-full justify-start h-12"
                    style={{ backgroundColor: brandColor }}
                    disabled={exporting}
                    data-testid="print-btn"
                  >
                    {exporting ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Printer className="w-4 h-4 mr-2" />
                    )}
                    {exporting ? "Préparation..." : "Imprimer"}
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
